import React from "react";
import { Card } from "./Card";
import { Button } from "./Button";
import { Pill } from "./Pill";
import { PillRow } from "./PillRow";
import { Badge } from "./Badge";
import { StatusRing } from "./StatusRing";
import { EmptyState } from "./EmptyState";
import { initials } from "../../lib/format";

// Small helper avatar for StatusRing showcase — defined at module scope
// so React doesn't recreate the component on every parent render.
function FakeAvatar({ name }) {
  return (
    <div className="avatar" style={{
      width: 48, height: 48, borderRadius: "50%",
      background: "var(--kc-elevated)",
      color: "var(--kc-text)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter',sans-serif", fontSize: 14, fontWeight: 700,
    }}>{initials(name || "")}</div>
  );
}

// Internal preview page for Phase-1 shared primitives. Rendered at
// `/internal/card-preview` for design QA. Not linked from the sidebar.
export default function CardPreview() {
  const [active, setActive] = React.useState("all");

  const variants = [
    { v: "green",  label: "Green — Active / Present / Completed / Confirmed" },
    { v: "blue",   label: "Blue — Scheduled / Calendar / Informational / Assigned" },
    { v: "amber",  label: "Amber — Visitor / Pending / Warning / Vacant" },
    { v: "red",    label: "Red — Absent / Urgent / Deletion / Rejected" },
    { v: "grey",   label: "Grey — Inactive / Left Church / Deceased / Archived" },
    { v: "purple", label: "Purple — Guest" },
    { v: "hero",   label: "Hero — Section hero / Empty state" },
    { v: "none",   label: "None — Base surface, no left border (default)" },
  ];

  const filterOptions = ["all", "active", "new member", "visitor", "guest", "inactive", "more"];

  const badgeSet = [
    { v: "green",  label: "Active" },
    { v: "green",  label: "Present" },
    { v: "green",  label: "Confirmed" },
    { v: "blue",   label: "Scheduled" },
    { v: "blue",   label: "Recurring" },
    { v: "amber",  label: "Visitor" },
    { v: "amber",  label: "Pending" },
    { v: "amber",  label: "One-time" },
    { v: "red",    label: "Absent" },
    { v: "red",    label: "Urgent" },
    { v: "grey",   label: "Inactive" },
    { v: "grey",   label: "Left" },
    { v: "purple", label: "Guest" },
    { v: "purple", label: "New Member" },
  ];

  const statuses = ["Active", "Visitor", "New Member", "Guest", "Inactive", "Left Church", "Deceased"];

  return (
    <div style={{ padding: "var(--space-lg)", background: "var(--kc-bg)", minHeight: "100vh" }}>
      <div className="text-eyebrow" style={{ marginBottom: "var(--space-sm)" }}>Phase 1 · Preview</div>
      <div className="text-page-title" style={{ marginBottom: "var(--space-lg)" }}>Shared Primitives</div>

      {/* ================================ CARDS ================================ */}
      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>Card Variants</div>
      <div style={{ display: "grid", gap: "var(--space-md)", maxWidth: 720, marginBottom: "var(--space-xl)" }}>
        {variants.map(({ v, label }) => (
          <Card key={v} variant={v} data-testid={`card-variant-${v}`}>
            <div className="text-badge" style={{ color: "inherit", marginBottom: "var(--space-xs)" }}>
              variant={`"${v}"`}
            </div>
            <div className="text-card-primary" style={{ color: "inherit" }}>{label}</div>
            <div className="text-card-secondary" style={{ marginTop: "var(--space-xs)", color: v === "hero" ? "inherit" : undefined }}>
              This card demonstrates the {v} variant styling.
            </div>
          </Card>
        ))}
        <Card variant="green" compact data-testid="card-variant-green-compact">
          <div className="text-card-primary">Compact green card — padding var(--space-sm).</div>
        </Card>
      </div>

      {/* ================================ BUTTONS ============================== */}
      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>Button Variants</div>
      <Card variant="none" data-testid="button-showcase" style={{ marginBottom: "var(--space-xl)" }}>
        <div className="text-eyebrow" style={{ marginBottom: "var(--space-sm)" }}>Default size</div>
        <div style={{ display: "flex", gap: "var(--space-md)", flexWrap: "wrap", alignItems: "center", marginBottom: "var(--space-lg)" }}>
          <Button variant="primary" data-testid="btn-primary">Save Changes</Button>
          <Button variant="secondary" data-testid="btn-secondary">View Details</Button>
          <Button variant="tertiary" data-testid="btn-tertiary">Cancel</Button>
          <Button variant="destructive" data-testid="btn-destructive">Delete Permanently</Button>
          <Button variant="icon" data-testid="btn-icon" aria-label="More">⋯</Button>
        </div>
        <div className="text-eyebrow" style={{ marginBottom: "var(--space-sm)" }}>Small size (primary + secondary)</div>
        <div style={{ display: "flex", gap: "var(--space-md)", flexWrap: "wrap", alignItems: "center", marginBottom: "var(--space-lg)" }}>
          <Button variant="primary" size="sm" data-testid="btn-primary-sm">Save</Button>
          <Button variant="secondary" size="sm" data-testid="btn-secondary-sm">Cancel</Button>
        </div>
        <div className="text-eyebrow" style={{ marginBottom: "var(--space-sm)" }}>Disabled state</div>
        <div style={{ display: "flex", gap: "var(--space-md)", flexWrap: "wrap", alignItems: "center" }}>
          <Button variant="primary" disabled data-testid="btn-primary-disabled">Disabled</Button>
          <Button variant="secondary" disabled data-testid="btn-secondary-disabled">Disabled</Button>
          <Button variant="destructive" disabled data-testid="btn-destructive-disabled">Disabled</Button>
        </div>
      </Card>

      {/* ================================ PILLS ================================ */}
      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>Filter Pills (horizontally scrollable, no wrap)</div>
      <Card variant="none" data-testid="pill-showcase" style={{ marginBottom: "var(--space-xl)" }}>
        <div className="text-eyebrow" style={{ marginBottom: "var(--space-sm)" }}>
          Selected: <span style={{ color: "var(--kc-text)" }}>{active}</span>
        </div>
        <PillRow data-testid="pill-row-flock">
          {filterOptions.map((opt) => (
            <Pill
              key={opt}
              active={active === opt}
              onClick={() => setActive(opt)}
              data-testid={`pill-${opt.replace(/\s+/g, "-")}`}
            >
              {opt}
            </Pill>
          ))}
        </PillRow>
        <div className="text-meta" style={{ marginTop: "var(--space-md)" }}>
          Active pill: indigo bg / white text in light, gold bg / near-black text in dark.
        </div>
      </Card>

      {/* ================================ BADGES =============================== */}
      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>Badge Variants</div>
      <Card variant="none" data-testid="badge-showcase" style={{ marginBottom: "var(--space-xl)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-sm)" }}>
          {badgeSet.map((b, i) => (
            <Badge
              key={`${b.v}-${b.label}-${i}`}
              variant={b.v}
              data-testid={`badge-${b.v}-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {b.label}
            </Badge>
          ))}
        </div>
        <div className="text-meta" style={{ marginTop: "var(--space-md)" }}>
          Badges use paired <code>--kc-*-bg</code> + <code>--kc-*</code> tokens so both flip automatically in dark mode.
        </div>
      </Card>

      {/* ================================ STATUS RINGS ========================= */}
      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>
        StatusRing — Privileged viewer (Super Admin / Limited Admin / Pastor)
      </div>
      <Card variant="none" data-testid="ring-privileged-showcase" style={{ marginBottom: "var(--space-xl)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-lg)", alignItems: "flex-start" }}>
          {statuses.map((s) => (
            <div key={s} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-xs)", minWidth: 88 }}>
              <StatusRing
                status={s}
                viewerRole="superadmin"
                data-testid={`ring-privileged-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <FakeAvatar name={s} />
              </StatusRing>
              <div className="text-meta">{s}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>
        StatusRing — Team Member viewer (rings suppressed)
      </div>
      <Card variant="none" data-testid="ring-team-showcase" style={{ marginBottom: "var(--space-xl)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-lg)", alignItems: "flex-start" }}>
          {statuses.map((s) => (
            <div key={s} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-xs)", minWidth: 88 }}>
              <StatusRing
                status={s}
                viewerRole="team_member"
                data-testid={`ring-team-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <FakeAvatar name={s} />
              </StatusRing>
              <div className="text-meta">{s}</div>
            </div>
          ))}
        </div>
        <div className="text-meta" style={{ marginTop: "var(--space-md)" }}>
          Rings are completely absent — Team Members never see status colouring on any avatar.
        </div>
      </Card>

      {/* ================================ EMPTY STATES ========================= */}
      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>EmptyState — Icon + Title only</div>
      <Card variant="none" data-testid="empty-basic" style={{ marginBottom: "var(--space-xl)" }}>
        <EmptyState
          icon="search"
          title="No results found"
          data-testid="empty-state-basic"
        />
      </Card>

      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>EmptyState — Icon + Title + Subtitle</div>
      <Card variant="none" data-testid="empty-subtitle" style={{ marginBottom: "var(--space-xl)" }}>
        <EmptyState
          icon="bell"
          title="You're all caught up"
          subtitle="No new notifications from the last 30 days. We'll let you know the moment something happens."
          data-testid="empty-state-subtitle"
        />
      </Card>

      <div className="text-section-header" style={{ marginBottom: "var(--space-md)" }}>EmptyState — Full (icon + title + subtitle + action)</div>
      <Card variant="none" data-testid="empty-full" style={{ marginBottom: "var(--space-xl)" }}>
        <EmptyState
          icon="people"
          title="No members yet"
          subtitle="Start building your church roster by adding your first member. You can also bulk-import from a CSV."
          action={{ label: "Add Member", onClick: () => window.alert("Add Member clicked") }}
          data-testid="empty-state-full"
        />
      </Card>
    </div>
  );
}
